import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import styles from './ChequeDetailsForm.styles';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import IChequeDetailsFormProps from './ChequeDetailsForm.properties';
import NumericField from '@markinson/uicomponents-v2/NumericField';
import TextField from '@markinson/uicomponents-v2/TextField';
import ITextFieldHandle from '@markinson/uicomponents-v2/TextField/TextField.handle';

const ChequeDetailsForm = (props: IChequeDetailsFormProps): JSX.Element => {
  const { classes, open, initialValues, schema, isProcessing, onApply, onCancel } = props;

  const [amount, setAmount] = React.useState<number>(0);
  const [chequeNumber, setChequeNumber] = React.useState<string>('');
  const [bankCode, setBankCode] = React.useState<string>('');
  const [branch, setBranch] = React.useState<string>('');
  const [accountName, setAccountName] = React.useState<string>('');
  const [bankAccountNumber, setBankAccountNumber] = React.useState<string>('');
  const [reference, setReference] = React.useState<string>('');

  const paymentAmountFieldRef = React.useRef<ITextFieldHandle>();
  const checqueFieldRef = React.useRef<ITextFieldHandle>();
  const bankFieldRef = React.useRef<ITextFieldHandle>();
  const branchFieldRef = React.useRef<ITextFieldHandle>();

  React.useEffect(
    () => {
      if (initialValues) {
        setAmount(initialValues.Amount ? initialValues.Amount : 0);
        setChequeNumber(initialValues.ChequeNumber ? initialValues.ChequeNumber.toString() : '');
        setBankCode(initialValues.BankCode);
        setBranch(initialValues.Branch);
        setAccountName(initialValues.AccountName);
        setBankAccountNumber(initialValues.BankAccountNumber);
        setReference(initialValues.Reference);
      } else {
        setAmount(0);
        setChequeNumber('');
        setBankCode('');
        setBranch('');
        setAccountName('');
        setBankAccountNumber('');
        setReference('');
      }
    },
    [initialValues]);

  const maxChequeNumberLength = 9;
  const maxChequeNumberParseLength = 20;

  const getModalContent = () => {
    return <div className={classes.mainDiv}>
      <div className={classes.leftColumnDiv}>
        <NumericField
          ref={paymentAmountFieldRef}
          value={amount}
          label={'Payment Amount'}
          minValueExclusive={0}
          onBlur={(value) => { setAmount(value); }}
          required={schema.Amount.Required} />
        <TextField
          innerRef={checqueFieldRef}
          value={chequeNumber}
          label={'Cheque'}
          maxLength={maxChequeNumberLength}
          onChange={(value) => { setChequeNumber(value); }}
          onBlur={(value) => {
            const parsedChequeNumber = parseInt(value ? value.substr(0, maxChequeNumberParseLength) : undefined);
            if (!isNaN(parsedChequeNumber)) {
              setChequeNumber(parsedChequeNumber.toString());
            } else {
              setChequeNumber('');
            }
          }}
          required={schema.ChequeNumber.Required} />
        <TextField
          innerRef={bankFieldRef}
          value={bankCode}
          label={'Bank'}
          onChange={(value) => { setBankCode(value); }}
          required={schema.BankCode.Required} />
        <TextField
          innerRef={branchFieldRef}
          value={branch}
          label={'Branch'}
          onChange={(value) => { setBranch(value); }}
          required={schema.Branch.Required} />
      </div>
      <div className={classes.rightColumnDiv}>
        <TextField
          value={accountName}
          label={'Account Name'}
          onChange={(value) => { setAccountName(value); }}
          required={schema.AccountName.Required} />
        <TextField
          value={bankAccountNumber}
          label={'Bank Account Number'}
          onChange={(value) => { setBankAccountNumber(value); }}
          required={schema.BankAccountNumber.Required} />
        <TextField
          value={reference}
          label={'Reference'}
          onChange={(value) => { setReference(value); }}
          required={schema.Reference.Required} />
      </div>
    </div >;
  };

  const confirmationActions = (): IActionItem[] => {
    return [
      {
        title: 'Ok',
        isDefault: true,
        disabled: isProcessing,
        listener: handleOnApply
      },
      {
        title: 'Cancel',
        listener: async () => {
          if (onCancel) {
            onCancel();
          }
        }
      }
    ];
  };

  const hasErrors = () => {
    if ((schema.Amount.Required && amount === undefined) || (amount !== undefined && amount <= 0) ||
      (schema.ChequeNumber.Required && !chequeNumber) || (chequeNumber && chequeNumber.length > maxChequeNumberLength) ||
      (schema.BankCode.Required && !bankCode) ||
      (schema.Branch.Required && !branch) ||
      (schema.AccountName.Required && !accountName) ||
      (schema.BankAccountNumber.Required && !bankAccountNumber) ||
      (schema.Reference.Required && !reference)) {

      return true;
    }

    return false;
  };

  const focusField = () => {
    setTimeout(
      () => {
        paymentAmountFieldRef?.current?.focus();
      },
      0
    );
  };

  const handleOnApply = async () => {
    if (!hasErrors()) {
      if (onApply) {
        onApply({
          ...initialValues,
          Amount: amount,
          ChequeNumber: parseInt(chequeNumber),
          BankCode: bankCode,
          Branch: branch,
          AccountName: accountName,
          BankAccountNumber: bankAccountNumber,
          Reference: reference
        });
      }
    } else {
      checqueFieldRef.current?.validate();
      bankFieldRef.current?.validate();
      branchFieldRef.current?.validate();
    }
  };

  React.useEffect(
    () => {
      const handleKeyDown = (event) => {
        const isCancelButton = event.target instanceof HTMLButtonElement && event.target.innerText === 'Cancel';

        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();

          if (isCancelButton && onCancel) {
            onCancel();

            return;
          }

          if (!hasErrors()) {
            handleOnApply();
          } else {
            checqueFieldRef.current?.validate();
            bankFieldRef.current?.validate();
            branchFieldRef.current?.validate();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    },
    [hasErrors, handleOnApply]
  );

  return (<FormViewModal
    open={open}
    loading={false}
    title={'Cheque Details'}
    modalContent={getModalContent()}
    actions={confirmationActions()}
    dialogActionsButtons={true}
    dialogActionsShadow={false}
    onActionShiftTabOut={focusField}
    onActionsTabOut={focusField}
  />);
};

export default withStyles(styles, { index: 1 })(ChequeDetailsForm);
